/**
 * The shared request helper. Almost the only place `fetch` is called — the one
 * exception is the upload in `resources.ts`, which needs progress events.
 *
 * Requests are always relative, so the same code works in production (where the
 * backend serves this bundle) and in development (where Vite proxies `/api`).
 * See ./README.md.
 */
import { API_BASE_PATH, type ApiErrorDetails, type ApiErrorResponse } from '@forsys/shared';

/** A non-2xx response from the API, carrying the backend's stable error code. */
export class ApiError extends Error {
  readonly status: number;
  /** Machine-readable identifier, e.g. `not_found`. Branch on this, not on the message. */
  readonly code: string;
  /** Structured data for the few codes that carry any, e.g. `self_lockout`. */
  readonly details: ApiErrorDetails | undefined;

  constructor(status: number, code: string, message: string, details?: ApiErrorDetails) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  /** Serialised as the JSON request body. Use instead of `body`. */
  json?: unknown;
}

/**
 * Listeners told that the session is gone.
 *
 * The session lives in an httpOnly cookie, so the app cannot see it expire —
 * it finds out when a request comes back 401. Rather than make every caller
 * handle that, the client announces it once and `AuthProvider` clears the
 * session, which sends the router to the sign-in screen.
 */
type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Registers a listener. Returns the function that removes it again. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

/**
 * Whether a 401 should be treated as "your session ended".
 *
 * It must not be during sign-in: a wrong password is also a 401, and announcing
 * it would tear down the screen the user is typing into. Callers that expect a
 * 401 as a legitimate answer pass `expectUnauthorized`.
 */
function announceUnauthorized() {
  for (const listener of unauthorizedListeners) listener();
}

/**
 * Listeners told that the server holds the caller to a password change.
 *
 * An administrator can require one while the user is working. Every request
 * then answers 403 `password_change_required` until it is done; announcing it
 * once lets `AuthProvider` re-read `GET /auth/me`, and the guard then shows the
 * change form instead of a screen full of errors.
 */
const passwordChangeListeners = new Set<UnauthorizedListener>();

/** Registers a listener. Returns the function that removes it again. */
export function onPasswordChangeRequired(listener: () => void): () => void {
  passwordChangeListeners.add(listener);
  return () => passwordChangeListeners.delete(listener);
}

export interface RequestOptions extends ApiFetchOptions {
  /**
   * Suppresses the session-expiry announcement for this request. Set it where a
   * 401 is an answer rather than an accident — signing in, and the very first
   * `GET /auth/me` that asks whether there is a session at all.
   */
  expectUnauthorized?: boolean;
}

/**
 * Calls the API and returns the parsed body.
 *
 * @param path Path *below* the API base, e.g. `/health` — not `/api/health`.
 * @throws {ApiError} on any non-2xx response.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { json, headers, expectUnauthorized, ...init } = options;

  const response = await fetch(`${API_BASE_PATH}${path}`, {
    ...init,
    // The session is a cookie. Same-origin in both modes — the backend serves
    // this bundle in production, Vite proxies /api in development — so the
    // browser sends it either way, but saying so keeps it true if that changes.
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
  });

  if (!response.ok) {
    if (response.status === 401 && !expectUnauthorized) announceUnauthorized();
    const error = new ApiError(response.status, ...(await readError(response)));
    if (error.code === 'password_change_required') {
      for (const listener of passwordChangeListeners) listener();
    }
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Pulls `[code, message, details]` out of a failed response.
 *
 * The body is not guaranteed to be our error shape — a proxy or the platform may
 * have produced it — so this must never throw.
 */
async function readError(
  response: Response,
): Promise<[code: string, message: string, details?: ApiErrorDetails]> {
  try {
    const body = (await response.json()) as Partial<ApiErrorResponse>;
    if (body.error?.code && body.error.message) {
      return [body.error.code, body.error.message, body.error.details];
    }
  } catch {
    // Not JSON, or truncated. Fall through to the generic message.
  }
  return ['unknown_error', `Request failed with status ${response.status}.`];
}

/** A query value that may legitimately be absent. */
export type QueryValue = string | number | boolean | undefined | null;

/**
 * Builds a query string, dropping anything absent.
 *
 * `undefined` and `null` are omitted, and so is the empty string — a filter
 * nobody filled in must not narrow the request to results whose field is empty.
 * Returns `''` when nothing survives, so it can always be appended.
 */
export function query(parameters: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(parameters)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

/**
 * The absolute path of an API endpoint, for the rare case a URL is needed
 * rather than a response: a download the browser performs itself.
 *
 * Still relative to the origin — this is `/api/...`, never a host.
 */
export function apiUrl(path: string): string {
  return `${API_BASE_PATH}${path}`;
}
